import React, { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { User, Mail, Lock, CheckCircle2, Droplet } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SampleForm = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/registrations`, {
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        opt_in: true,
      });
      toast.success("You're on the list — keep an eye on your inbox!");
      setDone(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check the form and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="relative rounded-[28px] bg-white border-2 border-[#fceadd] shadow-[0_24px_60px_-24px_rgba(229,25,127,0.30)] p-8 text-center"
        data-testid="registration-success"
      >
        <div className="mx-auto w-16 h-16 rounded-full bg-[#e5197f]/10 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-9 h-9 text-[#e5197f]" />
        </div>
        <h3 className="font-display text-2xl text-[#0b1b3d]">You&apos;re on the list!</h3>
        <p className="mt-3 text-[#0b1b3d]/75">
          We&apos;ll let you know the moment Hair Drips launches — and your free sample is on the way.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-[#e5197f] font-semibold">
          <Droplet className="w-4 h-4" /> Welcome to the drip.
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="relative rounded-[24px] bg-white/95 backdrop-blur-sm border border-[#fceadd] shadow-[0_24px_60px_-24px_rgba(229,25,127,0.30)] p-6 sm:p-7"
      data-testid="sample-form"
    >
      <div className="space-y-4">
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#e5197f]" />
          <Input
            data-testid="input-full-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Your name"
            className="pl-12 h-14 rounded-2xl border-2 border-[#fceadd] bg-white focus-visible:ring-2 focus-visible:ring-[#e5197f]/30 focus-visible:border-[#e5197f] text-base placeholder:text-[#c5a89a]"
            autoComplete="name"
          />
        </div>

        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#e5197f]" />
          <Input
            data-testid="input-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            className="pl-12 h-14 rounded-2xl border-2 border-[#fceadd] bg-white focus-visible:ring-2 focus-visible:ring-[#e5197f]/30 focus-visible:border-[#e5197f] text-base placeholder:text-[#c5a89a]"
            autoComplete="email"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="registration-submit-button"
          className="w-full h-14 rounded-full bg-[#e5197f] hover:bg-[#c4156c] text-white font-bold tracking-[0.25em] text-base shadow-[0_10px_30px_-8px_rgba(229,25,127,0.65)] hover:-translate-y-0.5 transition-all"
        >
          {loading ? "Sending..." : "SIGN UP"}
        </Button>

        <div className="flex items-center justify-center gap-2 text-xs text-[#0b1b3d]/60 mt-2">
          <Lock className="w-3.5 h-3.5" /> We respect your privacy. No spam, ever.
        </div>
      </div>
    </form>
  );
};

export default SampleForm;
